import Vue from "vue";
import axios from "axios";
import router from "@/router/";
import store from "@/store";

const API_ENDPOINT = process.env.VUE_APP_API_ENDPOINT;

const devInstance = createInstance(API_ENDPOINT);

let requestInterval = null;
let isRefreshing = false;
let pendingRequests = [];

// Admin access tokens are short-lived (15 min). On a 403 we try the refresh token once before giving up and sending the user back to login.
async function tryRefreshAndRetry(originalConfig) {
  const refreshToken = sessionStorage.getItem("refresh_jwt");
  if (!refreshToken) return null;

  // Never try to refresh the refresh call itself
  if (originalConfig.url && originalConfig.url.includes("refresh-token")) {
    return null;
  }

  // A refresh is already running - wait for it, then replay this request
  if (isRefreshing) {
    return new Promise((resolve) => pendingRequests.push(resolve)).then(() =>
      devInstance(originalConfig)
    );
  }

  isRefreshing = true;
  try {
    // Plain axios, not devInstance: this must bypass the interceptor
    const res = await axios.post(
      `${API_ENDPOINT}/refresh-token`,
      { refresh_token: refreshToken },
      { headers: { "Content-Type": "application/json" } }
    );
    sessionStorage.setItem("jwt", res.data.token);
    sessionStorage.setItem("refresh_jwt", res.data.refresh_token);
    pendingRequests.forEach((cb) => cb());
    pendingRequests = [];
    return devInstance(originalConfig);
  } catch (e) {
    pendingRequests.forEach((cb) => cb());
    pendingRequests = [];
    return null;
  } finally {
    isRefreshing = false;
  }
}

devInstance.interceptors.request.use(
  (config) => {
    config.validateStatus = function (status) {
      return status < 500;
    };

    var jwt = sessionStorage.getItem("jwt");
    if (jwt != null) {
      config.headers["Authorization"] = "Bearer " + jwt;
    }
    config.headers["lang"] = store.state.user.language;
    config.headers["timezone"] =
      Intl.DateTimeFormat().resolvedOptions().timeZone;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

devInstance.interceptors.response.use(
  async function (response) {
    // Check if this request should skip auth redirect and error handling
    const skipAuthRedirect = response.config.skipAuthRedirect;

    if (response?.status === 403) {
      if (!skipAuthRedirect && !response.config._retry) {
        response.config._retry = true;

        const retried = await tryRefreshAndRetry(response.config);
        if (retried) {
          return retried;
        }

        Vue.prototype.$toast.open({
          position: "top",
          type: "error",
          message: response?.data.message,
        });
        router.push("/");
        return;
      }
      // If skipAuthRedirect is true, just continue without redirecting
    }
    if (response?.status > 299 && !skipAuthRedirect) {
      store.commit("SET_TOAST_MESSAGE", response?.data.message);
      store.commit("SET_TOAST_STATE", true);
      store.commit("SET_TOAST_TYPE", "error");
      setTimeout(() => {
        store.commit("SET_TOAST_STATE", false);
        store.commit("SET_TOAST_TYPE", "info");
      }, 1500);
    }

    if (response.config.url === "dzi/") {
      clearTimeout(requestInterval);
    }

    store.commit("SET_IS_LOADING_SYNC", false);

    setTimeout(() => {
      if (response?.status === 403 && response?.data.errorCode == "expired") {
        router.push("/subscription");
      }

      if (
        response?.status === 403 &&
        response?.data.errorCode == "over_due_payment"
      ) {
        router.push("/invoices");
      }
    }, 2000);

    return response;
  },
  function (err) {
    // Check if this request should skip auth redirect and error handling
    const skipAuthRedirect = err.config?.skipAuthRedirect;

    if (err.response?.status > 299 && !skipAuthRedirect) {
      store.commit("SET_TOAST_MESSAGE", err.response?.data.message);
      store.commit("SET_TOAST_STATE", true);
      setTimeout(() => {
        store.commit("SET_TOAST_STATE", false);
      }, 1500);
    }

    if (
      !skipAuthRedirect &&
      router.history.current.name !== "Login" &&
      err.response?.status === 403
    ) {
      switch (err.response.data.code) {
        case "E_BOOKMARK_NO_PERMISSION":
          this.$showToastWithType(
            this.$i18n.t("message.error.noBookmarkPermission"),
            "error"
          );
          break;
        case "NO_SELF_ROLE_CHANGE":
          this.$showToastWithType(
            this.$i18n.t("message.error.noSelfRoleChange"),
            "error"
          );
          break;
        default:
          this.$showToastWithType(
            this.$i18n.t("message.error.noPagePermission"),
            "error"
          );
          // router.push("/");
          break;
      }
    }
    return Promise.reject(err);
  }
);

function createInstance(baseURL) {
  return axios.create({
    baseURL,
    withCredentials: true,
    headers: {
      "Content-Type": "application/json",
    },
  });
}

export const httpInstance = devInstance;

export default {
  install() {
    Vue.prototype.$http = devInstance;
  },
};
